import * as React from 'react';

import { useTheme } from '@mui/material/styles';

import { Puller } from '@modules/_shared/components/puller';
import { useWindowInnerSize } from '@shared/hooks/useWindowInnerSize';
import { useTheme as useCustomTheme } from '@shared/theme/theme';

const MOBILE_SHEET_HALF_RATIO = 0.55;
const MOBILE_SHEET_MIN_HEIGHT = 320;
const MOBILE_SHEET_CLOSE_RATIO = 0.38;
const MOBILE_SHEET_VELOCITY_THRESHOLD = 0.55;

const clamp = (value: number, min: number, max: number): number =>
  Math.min(Math.max(value, min), max);

export const ChatMobileSheet = ({
  containerRef,
  onClose,
  children,
}: {
  containerRef: React.RefObject<HTMLDivElement | null>;
  onClose: () => void;
  children: React.ReactNode;
}) => {
  const theme = useTheme();
  const { colors } = useCustomTheme();
  const [, viewportHeight] = useWindowInnerSize();
  const [dragging, setDragging] = React.useState(false);
  const [sheetHeight, setSheetHeight] = React.useState(0);
  const dragStateRef = React.useRef<{
    pointerId: number;
    startY: number;
    startHeight: number;
    lastY: number;
    lastTime: number;
    velocity: number;
  } | null>(null);

  const containerHeight = Math.max(
    containerRef.current?.clientHeight ?? 0,
    viewportHeight,
  );
  const halfHeight = clamp(
    Math.round(containerHeight * MOBILE_SHEET_HALF_RATIO),
    MOBILE_SHEET_MIN_HEIGHT,
    containerHeight,
  );
  const fullHeight = containerHeight;
  const closeThreshold = Math.max(
    MOBILE_SHEET_MIN_HEIGHT * MOBILE_SHEET_CLOSE_RATIO,
    halfHeight * MOBILE_SHEET_CLOSE_RATIO,
  );
  const isFullScreen = sheetHeight >= fullHeight - 8;

  React.useEffect(() => {
    setSheetHeight(halfHeight);
  }, [halfHeight]);

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.pointerType === 'mouse' && e.button !== 0) {
      return;
    }
    setDragging(true);
    dragStateRef.current = {
      pointerId: e.pointerId,
      startY: e.clientY,
      startHeight: sheetHeight || halfHeight,
      lastY: e.clientY,
      lastTime: e.timeStamp,
      velocity: 0,
    };
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const dragState = dragStateRef.current;
    if (!dragState || dragState.pointerId !== e.pointerId) {
      return;
    }

    const delta = dragState.startY - e.clientY;
    const nextHeight = clamp(dragState.startHeight + delta, 0, fullHeight);
    const elapsed = Math.max(e.timeStamp - dragState.lastTime, 1);
    dragState.velocity = (dragState.lastY - e.clientY) / elapsed;
    dragState.lastY = e.clientY;
    dragState.lastTime = e.timeStamp;
    setSheetHeight(nextHeight);
  };

  const handlePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    const dragState = dragStateRef.current;
    if (!dragState || dragState.pointerId !== e.pointerId) {
      return;
    }

    e.currentTarget.releasePointerCapture(e.pointerId);
    dragStateRef.current = null;
    setDragging(false);

    if (
      sheetHeight < closeThreshold ||
      (dragState.velocity < -MOBILE_SHEET_VELOCITY_THRESHOLD &&
        sheetHeight < halfHeight + 60)
    ) {
      onClose();
      return;
    }

    if (
      sheetHeight > (halfHeight + fullHeight) / 2 ||
      dragState.velocity > MOBILE_SHEET_VELOCITY_THRESHOLD
    ) {
      setSheetHeight(fullHeight);
      return;
    }

    setSheetHeight(halfHeight);
  };

  return (
    <div
      // Inside the page area: read_current_page skips it (currentPage.ts).
      data-chat-panel=""
      css={{
        position: 'absolute',
        inset: 0,
        zIndex: theme.zIndex.drawer + 2,
        pointerEvents: 'none',
      }}
    >
      <button
        type="button"
        aria-label="Close chat"
        onClick={onClose}
        css={{
          position: 'absolute',
          inset: 0,
          border: 'none',
          background: `rgba(15, 23, 42, ${isFullScreen ? 0.22 : 0.16})`,
          padding: 0,
          margin: 0,
          cursor: 'pointer',
          pointerEvents: 'auto',
        }}
      />
      <div
        css={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 0,
          height: sheetHeight,
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: colors.superLightBlue,
          borderTopLeftRadius: isFullScreen ? 0 : 20,
          borderTopRightRadius: isFullScreen ? 0 : 20,
          boxShadow: '0 -12px 40px rgba(15, 23, 42, 0.18)',
          overflow: 'hidden',
          pointerEvents: 'auto',
          transition: dragging ? 'none' : 'height 0.22s ease',
          touchAction: 'none',
        }}
      >
        <div
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
          css={{
            position: 'absolute',
            top: 0,
            left: '50%',
            transform: 'translateX(-50%)',
            width: 120,
            height: 28,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'flex-start',
            cursor: dragging ? 'grabbing' : 'grab',
            touchAction: 'none',
            zIndex: 2,
          }}
        >
          <Puller css={{ marginTop: 9 }} width={42} height={5} />
        </div>
        <div css={{ flex: 1, minHeight: 0 }}>{children}</div>
      </div>
    </div>
  );
};
